import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, CheckCircle, XCircle } from "lucide-react";
import api from "@/lib/api";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: ""
  });
  const [errors, setErrors] = useState({});

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      setVerifying(false);
      return;
    }

    api.get(`/auth/verify-reset-token/${token}`)
      .then((res) => {
        setTokenValid(true);
        setEmail(res.data.email);
      })
      .catch((err) => {
        setTokenValid(false);
      })
      .finally(() => setVerifying(false));
  }, [token]);

  const validatePassword = (password) => {
    if (!password) return "Password is required";
    if (password.length < 8) return "Password must be at least 8 characters";
    if (!/[a-z]/.test(password)) return "Password must contain a lowercase letter";
    if (!/[A-Z]/.test(password)) return "Password must contain an uppercase letter";
    if (!/\d/.test(password)) return "Password must contain a number";
    return null;
  };

  const validateForm = () => {
    const newErrors = {};
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      newErrors.password = passwordError;
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords don't match";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: formData.password
      });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to reset password";
      toast.error(message);
      setErrors({ submit: message });
    } finally {
      setLoading(false);
    }
  };

  // Loading state while verifying token
  if (verifying) {
    return (
      <div className="flex items-center justify-center px-4 py-20" data-testid="reset-password-loading">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-stone-300 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="text-stone-500">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // No token provided
  if (!token) {
    return (
      <div className="flex items-center justify-center px-4 py-20" data-testid="reset-password-no-token">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="font-heading font-bold text-xl text-stone-900 mb-2">Invalid Reset Link</h1>
          <p className="text-stone-500 text-sm mb-6">
            This password reset link is invalid. Please request a new one.
          </p>
          <Link to="/auth">
            <Button className="rounded-full bg-coral hover:bg-coral text-white">
              Back to Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Token invalid or expired
  if (!tokenValid) {
    return (
      <div className="flex items-center justify-center px-4 py-20" data-testid="reset-password-invalid">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="font-heading font-bold text-xl text-stone-900 mb-2">Link Expired</h1>
          <p className="text-stone-500 text-sm mb-6">
            This password reset link has expired or already been used. Please request a new one.
          </p>
          <Link to="/auth">
            <Button className="rounded-full bg-coral hover:bg-coral text-white">
              Back to Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="flex items-center justify-center px-4 py-20" data-testid="reset-password-success">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="font-heading font-bold text-xl text-stone-900 mb-2">Password Reset!</h1>
          <p className="text-stone-500 text-sm mb-6">
            Your password has been successfully reset. You can now log in with your new password.
          </p>
          <Link to="/auth">
            <Button className="rounded-full bg-coral hover:bg-coral text-white">
              Log In
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="flex items-center justify-center px-4 py-12" data-testid="reset-password-form">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Paradise Borrowed" className="w-20 h-auto mx-auto mb-4" />
          <h1 className="font-heading font-bold text-2xl text-stone-900">Set New Password</h1>
          <p className="text-stone-500 text-sm mt-1">
            Enter a new password for <span className="font-medium text-stone-700">{email}</span>
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {errors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                {errors.submit}
              </div>
            )}

            <div>
              <Label htmlFor="password">New Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="Min 8 chars, uppercase, lowercase, number"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={`h-12 pl-10 pr-10 rounded-xl ${errors.password ? 'border-red-300' : ''}`}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
            </div>

            <div>
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="Confirm new password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className={`h-12 pl-10 rounded-xl ${errors.confirmPassword ? 'border-red-300' : ''}`}
                  data-testid="confirm-password-input"
                />
              </div>
              {errors.confirmPassword && <p className="text-xs text-red-500 mt-1">{errors.confirmPassword}</p>}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-coral hover:bg-coral text-white btn-press"
              data-testid="reset-password-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Resetting...
                </span>
              ) : (
                "Reset Password"
              )}
            </Button>
          </form>

          <p className="text-xs text-stone-400 text-center mt-4">
            Remember your password?{" "}
            <Link to="/auth" className="text-coral hover:underline">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
